import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';

import * as AdminUserActions from './admin-user.actions';
import { AdminUserEntity } from './admin-user.models';

export const ADMIN_USER_FEATURE_KEY = 'admin';

export interface State extends EntityState<AdminUserEntity> {
  loading: boolean;
  error?: any;
  resetPassModal: {
    open: boolean;
    loading: boolean;
  };
  addAdminUser: {
    loading: boolean;
    open: boolean;
  };
}

export interface AdminPartialState {
  readonly [ADMIN_USER_FEATURE_KEY]: State;
}

export function selectAdminUserId(adminUser: AdminUserEntity): string {
  return adminUser._id;
}

export const adminUserAdapter: EntityAdapter<AdminUserEntity> =
  createEntityAdapter<AdminUserEntity>({
    selectId: selectAdminUserId
  });

export const initialState: State = adminUserAdapter.getInitialState({
  loading: false,
  resetPassModal: {
    loading: false,
    open: false
  },
  addAdminUser: {
    loading: false,
    open: false
  }
});

const adminUserReducer = createReducer(
  initialState,

  /* ------------------------------ getAdminUsers ----------------------------- */

  on(AdminUserActions.getAdminUsers, state => ({
    ...state,
    loading: true,
    error: null
  })),
  on(AdminUserActions.getAdminUsersSuccess, (state, { adminUsers, query }) =>
    adminUserAdapter.setAll(adminUsers, {
      ...state,
      loading: false
    })
  ),
  on(AdminUserActions.getAdminUsersFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false
  })),

  /* ---------------------------- Create admin user --------------------------- */

  on(AdminUserActions.createAdminUser, (state, { adminUser }) => ({
    ...state,
    loading: true,
    error: null,
    addAdminUser: { ...state.addAdminUser, loading: true }
  })),
  on(AdminUserActions.createAdminUserSuccess, (state, { adminUser }) =>
    adminUserAdapter.addOne(adminUser, {
      ...state,
      loading: false,
      error: null,
      addAdminUser: { ...state.addAdminUser, loading: false, open: false }
    })
  ),
  on(AdminUserActions.createAdminUserFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false,
    addAdminUser: { ...state.addAdminUser, loading: false }
  })),
  on(AdminUserActions.startAddAdminUser, state => ({
    ...state,
    addAdminUser: { ...state.addAdminUser, open: true }
  })),
  on(AdminUserActions.cancelAddAdminUser, state => ({
    ...state,
    addAdminUser: { ...state.addAdminUser, open: false }
  })),

  /* ---------------------------- Update admin user --------------------------- */

  // loading in edit user is handled for each user

  on(AdminUserActions.updateAdminUser, (state, { adminUser }) =>
    adminUserAdapter.updateOne(
      { id: adminUser._id, changes: { loading: true } },
      { ...state }
    )
  ),
  on(AdminUserActions.updateAdminUserSuccess, (state, { adminUser }) =>
    adminUserAdapter.updateOne(
      {
        id: adminUser._id,
        changes: { ...adminUser, loading: false, edit: false }
      },
      {
        ...state,
        error: null
      }
    )
  ),
  on(AdminUserActions.updateAdminUserFailure, (state, { error, adminUserId }) =>
    adminUserAdapter.updateOne(
      { id: adminUserId, changes: { loading: false } },
      { ...state, error }
    )
  ),
  on(AdminUserActions.startEditAdminUser, (state, { adminUserId }) =>
    adminUserAdapter.updateOne(
      { id: adminUserId, changes: { edit: true } },
      { ...state }
    )
  ),
  on(AdminUserActions.cancelEditAdminUser, (state, { adminUserId }) =>
    adminUserAdapter.updateOne(
      { id: adminUserId, changes: { edit: false } },
      { ...state }
    )
  ),

  /* ---------------------------- Delete admin user --------------------------- */

  on(AdminUserActions.deleteAdminUser, (state, { adminUserId }) => ({
    ...state,
    loading: true,
    error: null
  })),
  on(AdminUserActions.deleteAdminUserSuccess, (state, { adminUserId }) =>
    adminUserAdapter.removeOne(adminUserId, {
      ...state,
      loading: false,
      error: null
    })
  ),
  on(AdminUserActions.deleteAdminUserFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false
  })),

  /* ------------------------ Reset admin user password ----------------------- */
  on(AdminUserActions.resetAdminUserPassword, state => ({
    ...state,
    loading: true,
    error: null,
    resetPassModal: {
      ...state.resetPassModal,
      loading: true
    }
  })),
  on(AdminUserActions.resetAdminUserPasswordSuccess, state => ({
    ...state,
    loading: false,
    error: null,
    resetPassModal: {
      ...state.resetPassModal,
      loading: false,
      open: false
    }
  })),
  on(AdminUserActions.resetAdminUserPasswordFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false,
    resetPassModal: {
      ...state.resetPassModal,
      loading: false
    }
  })),
  on(AdminUserActions.openResetAdminUserPasswordModal, state => ({
    ...state,
    resetPassModal: { ...state.resetPassModal, open: true }
  })),
  on(AdminUserActions.closeResetAdminUserPasswordModal, state => ({
    ...state,
    resetPassModal: { ...state.resetPassModal, open: false }
  }))
);

export function reducer(state: State | undefined, action: Action) {
  return adminUserReducer(state, action);
}
